import Head from 'next/head';

import { ReactElement, useEffect, useState } from 'react';

import { ColorModeScript } from '@chakra-ui/react';
import { setDefaultOptions } from 'date-fns';

import { ErrorBoundary } from 'components/ErrorBoundary';
import { AppLayout } from 'components/layouts';
import { theme, ThemeProvider } from 'components/theme';
import { Loader } from 'components/uikit';
import { useAccessGuard } from 'data/auth/hooks/useAccessGuard';
import { dateFnsDefaultConfig } from 'data/constants/dateFns';
import 'locales/i18n';
import { useGetInitialColorMode } from 'utils/hooks/useGetInitialColorMode';
import { AppPropsWithLayout, EntryPoint } from 'utils/nextTypes';

setDefaultOptions(dateFnsDefaultConfig);

function AppHead() {
  return (
    <Head>
      <title>PayTop</title>
      {/* <!-- Primary Meta Tags --> */}
      <title>PayTop | Credit card crypto acquiring</title>
      <meta name='title' content='PayTop | Credit card crypto acquiring' />
      <meta
        name='description'
        content='PayTop is fast and stable way for your clients to pay in fiat and you get the crypto'
      />

      {/* <!-- https://developer.mozilla.org/en-US/docs/Web/HTML/Element/meta/name/theme-color --> */}
      <meta
        name='theme-color'
        media='(prefers-color-scheme: light)'
        content='#EEF2F6'
      />
      <meta
        name='theme-color'
        media='(prefers-color-scheme: dark)'
        content='#090D13'
      />

      {/*{ <!-- Open Graph / Facebook -->}*/}
      <meta property='og:type' content='website' />
      <meta property='og:url' content='https://payment.paytop.pro/' />
      <meta
        property='og:title'
        content='PayTop | Credit card crypto acquiring'
      />
      <meta
        property='og:description'
        content='PayTop is fast and stable way for your clients to pay in fiat and you get the crypto'
      />
      <meta
        property='og:image'
        /*TODO: add og image*/
        content=''
      />

      {/*{!--Twitter--}*/}
      <meta property='twitter:card' content='summary_large_image' />
      <meta property='twitter:url' content='https://paytop.pro/' />
      <meta
        property='twitter:title'
        content='PayTop | Credit card crypto acquiring'
      />
      <meta
        property='twitter:description'
        content='PayTop is fast and stable way for your clients to pay in fiat and you get the crypto'
      />
      <meta
        property='twitter:image'
        /*TODO: add twitter image*/
        content=''
      />
      <link
        rel='apple-touch-icon'
        sizes='180x180'
        href='/static/favicon/apple-touch-icon.png'
      />
      <link
        rel='icon'
        type='image/png'
        sizes='32x32'
        href='/static/favicon/favicon-32x32.ico'
      />
      <link
        rel='icon'
        type='image/png'
        sizes='16x16'
        href='/static/favicon/favicon-16x16.ico'
      />
      <link rel='manifest' href='/static/favicon/site.webmanifest' />
    </Head>
  );
}

const defaultPageRenderer = (page: ReactElement) => page;
const loaderRenderer = () => <Loader />;

interface AppProps extends AppPropsWithLayout {
  userLoaded: boolean;
  initialAccessPath?: string;
}

export function App({
  Component,
  pageProps,
  userLoaded,
  initialAccessPath,
}: AppProps) {
  const [isMounted, setIsMounted] = useState(false);
  useGetInitialColorMode();

  useAccessGuard(Component.entryPointType, initialAccessPath);

  const getLayout =
    !userLoaded && Component.entryPointType === EntryPoint.PROTECTED
      ? loaderRenderer
      : Component.getLayout ?? defaultPageRenderer;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return loaderRenderer();
  }

  return (
    <>
      <ThemeProvider>
        <AppHead />
        <AppLayout>
          <ErrorBoundary>
            <ColorModeScript initialColorMode={theme.config.initialColorMode} />
            {getLayout(<Component {...pageProps} />)}
          </ErrorBoundary>
        </AppLayout>
      </ThemeProvider>
    </>
  );
}
